import { Injectable, inject } from '@angular/core';
import { Route } from '@app/core/enums/route.enum';
import { MaintenanceModalComponent } from '@app/maintenance/maintenance-modal/maintenance-modal.component';
import { App } from '@capacitor/app';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { BehaviorSubject, filter, interval, withLatestFrom, startWith, combineLatest, map, firstValueFrom } from 'rxjs';
import { Maintenance, MaintenanceAnnouncement } from '../types/maintenance/maintenance.types';
import { AppConfigService } from './config.service';
import { PluginListenerHandle } from '@capacitor/core';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  authenticationService = inject(AuthenticationService);

  private maintenance$ = new BehaviorSubject<Maintenance>(null);

  currentActiveMaintenance$ = new BehaviorSubject<MaintenanceAnnouncement>(null);
  appInForeground$ = new BehaviorSubject<boolean>(true);
  stateChangeListener: PluginListenerHandle;
  announcementModalShownFor: DateTime;

  private readonly router = inject(Router);

  constructor(
    private configService: AppConfigService,
    private modalController: ModalController,
  ) {}

  async initMaintenanceChecks(): Promise<void> {
    this.loadMaintenanceConfig();
    combineLatest([this.maintenance$, interval(1000 * 60).pipe(startWith(0))])
      .pipe(
        map(([maintenanceData]) => maintenanceData),
        withLatestFrom(this.appInForeground$),
        filter(([, inForeground]) => inForeground)
      )
      .subscribe(([maintenanceData]) => {
        this.checkMaintenance(maintenanceData);
      });

    this.stateChangeListener = await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.loadMaintenanceConfig();
      }
      this.appInForeground$.next(isActive);
    });
  }

  private loadMaintenanceConfig(): void {
    this.configService.getMaintenanceConfig().subscribe((config) => {
      this.maintenance$.next({
        ...config?.maintenance,
        times: config?.maintenance?.times.map((t) => ({
          start: DateTime.fromISO(t.start, { zone: 'utc' }),
          end: DateTime.fromISO(t.end, { zone: 'utc' }),
          announcement: t.announcement,
        })),
      });
    });
  }

  private async checkMaintenance(maintenanceData: Maintenance): Promise<void> {
    const currentTime = DateTime.utc().startOf('minute');
    const isAuthenticated = await firstValueFrom(this.authenticationService.authenticated$);
    let foundMaintenanceWindow = false;

    maintenanceData?.times?.forEach(async (maintenance) => {
      const start = maintenance.start.startOf('minute');
      const end = maintenance.end.startOf('minute');

      if (currentTime >= start && currentTime <= end) {
        foundMaintenanceWindow = true;
        this.currentActiveMaintenance$.next(maintenance);
        this.router.navigate([Route.Maintenance]);
        return;
      }

      const shouldShowAnnouncement =
        +currentTime === +start.minus({ minute: maintenanceData.showAnnouncementMinutesBefore });
      const modalAlreadyShownForThisMaintenance = +this.announcementModalShownFor === +start;
      if (isAuthenticated && shouldShowAnnouncement && !modalAlreadyShownForThisMaintenance) {
        await this.openAnnouncementModal(maintenance, maintenanceData.showAnnouncementMinutesBefore);
        return;
      }
    });

    if (!foundMaintenanceWindow && !this.currentActiveMaintenance$.value) {
      return;
    }

    if (!foundMaintenanceWindow && !!this.currentActiveMaintenance$.value) {
      this.currentActiveMaintenance$.next(null);
      this.router.navigate([Route.Home]);
    }
  }

  private async openAnnouncementModal(maintenance: MaintenanceAnnouncement, minutes: number): Promise<void> {
    const announcementModalId = 'announcementModal';
    const topModal = await this.modalController.getTop();
    if (topModal?.id === announcementModalId) {
      return;
    }
    const modal = await this.modalController.create({
      component: MaintenanceModalComponent,
      cssClass: ['modal'],
      id: announcementModalId,
      componentProps: {
        close: () => this.modalController.dismiss(),
        minutes,
        maintenance,
      },
    });
    await modal.present();
    this.announcementModalShownFor = maintenance.start;
  }
}
